import { zodResolver } from "@hookform/resolvers/zod";
import type { TaskStatusOutput } from "@orsane/schema/tasks";
import { CircleDashed } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { statusColors } from "@/app/colors";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpcClient } from "@/lib/trpc";
import { useCreateTaskStatusMutation, useUpdateTaskStatusMutation } from "@/mutations/mutations";
import EnterButton from "../buttons/EnterButton";
import LoadingSpinner from "../loading-spinner";
import { Field, FieldError, FieldLabel } from "../ui/field";

const statusColorValues = [
    "gray",
    "red",
    "orange",
    "amber",
    "lime",
    "green",
    "emerald",
    "teal",
    "cyan",
    "blue",
    "sky",
    "indigo",
    "purple",
    "pink",
    "rose",
] as const;

const taskStatusFormSchema = z.object({
    name: z.string().min(1, "Name is required").max(100, "Name cannot exceed 100 characters"),
    color: z.enum(statusColorValues).nullable().optional(),
});

type TaskStatusFormData = z.infer<typeof taskStatusFormSchema>;

interface TaskStatusDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    status?: TaskStatusOutput | "new" | null;
    onSubmit: () => void;
}

export function TaskStatusDialog({ open, onOpenChange, status, onSubmit }: TaskStatusDialogProps) {
    const { mutateAsync: createTaskStatus, isPending: isCreating } = useCreateTaskStatusMutation();
    const { mutateAsync: updateTaskStatus, isPending: isUpdating } = useUpdateTaskStatusMutation();
    const [isLoading, setIsLoading] = useState(false);
    const isEditing = status !== "new";

    const form = useForm<TaskStatusFormData>({
        resolver: zodResolver(taskStatusFormSchema),
        defaultValues: {
            name: "",
            color: "gray",
        },
    });

    useEffect(() => {
        async function fetchStatus(id: number) {
            setIsLoading(true);
            const fetchedStatus = await trpcClient.taskStatus.get.query({ id: id });
            if (fetchedStatus) {
                form.reset({
                    name: fetchedStatus.name,
                    color: (fetchedStatus.color as (typeof statusColorValues)[number]) ?? null,
                });
                setIsLoading(false);
            }
        }

        if (open) {
            // first reset the form
            form.reset({
                name: "",
                color: "gray",
            });

            if (status && status !== "new") {
                fetchStatus(status.id);
            } else {
                setIsLoading(false);
            }
        }
    }, [status, open, form]);

    const handleSubmit = async (data: TaskStatusFormData) => {
        if (status === "new") {
            await createTaskStatus(data);
        } else {
            const dto = { ...data, id: status!.id };
            await updateTaskStatus(dto);
        }

        onSubmit();
    };

    const isAllowedToSave = form.formState.isValid && !isLoading;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px] gap-6 rounded-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg font-medium">
                        <CircleDashed className="size-4" />
                        {isEditing ? "Edit Task Status" : "Create Task Status"}
                    </DialogTitle>
                </DialogHeader>
                {isLoading && <LoadingSpinner />}
                {!isLoading && (
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                        <Controller
                            control={form.control}
                            name="name"
                            render={({ field, fieldState }) => (
                                <Field>
                                    <FieldLabel>Name</FieldLabel>
                                    <Input autoFocus data-1p-ignore placeholder="Status name" {...field} />
                                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                                </Field>
                            )}
                        />

                        {status !== null && !status?.isSystem && (
                            <Controller
                                control={form.control}
                                name="color"
                                render={({ field, fieldState }) => (
                                    <Field>
                                        <FieldLabel>Color</FieldLabel>
                                        <Select
                                            onValueChange={(value) => field.onChange(value === "none" ? null : value)}
                                            value={field.value ?? "none"}
                                        >
                                            <SelectTrigger className="min-w-36">
                                                <SelectValue>
                                                    <div
                                                        className={`w-3 h-3 rounded-full ${statusColors[field.value as keyof typeof statusColors]?.darkerClassName}`}
                                                    />
                                                    {field.value
                                                        ? (statusColors[field.value as keyof typeof statusColors]
                                                              ?.name ?? field.value)
                                                        : "None"}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    <SelectItem value="none">None</SelectItem>
                                                    {statusColorValues.map((color) => {
                                                        const colorInfo = statusColors[color];
                                                        return (
                                                            <SelectItem key={color} value={color}>
                                                                <div className="flex items-center gap-2">
                                                                    <div
                                                                        className={`w-3 h-3 rounded-full ${colorInfo.darkerClassName}`}
                                                                    />
                                                                    {colorInfo.name}
                                                                </div>
                                                            </SelectItem>
                                                        );
                                                    })}
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                        {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                                    </Field>
                                )}
                            />
                        )}

                        <DialogFooter>
                            <Button
                                size="sm"
                                type="button"
                                disabled={isLoading}
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <EnterButton
                                type="submit"
                                disabled={!isAllowedToSave}
                                isSubmitting={isCreating || isUpdating}
                            >
                                {isEditing ? "Update" : "Create"}
                            </EnterButton>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
